import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { track, trackAdConversionOnce, getSignupSource } from "@/lib/analytics";
import { useT, getStoredLang } from "@/lib/i18n";

const SIGNUP_ADS_CONVERSION = "AW-18165868123/L5kHCMb7nLEcENvM1NzD";

function googleLoginRedirect() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Register() {
  const [form, setForm] = useState({ name: "", company_name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const { registerEmail, user, loading } = useAuth();
  const navigate = useNavigate();
  const { t, lang } = useT();

  useEffect(() => {
    if (!loading && user) navigate("/app", { replace: true });
  }, [loading, user, navigate]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Pass current landing language so the new account is created with the
      // right `language` preference (en if signing up from verasoreports.com / EN landing).
      const langForSignup = getStoredLang() || lang || "fi";
      const newUser = await registerEmail({ ...form, language: langForSignup });
      // Attribution: read the source bundle stashed at landing time so
      // Plausible can report registrations grouped by source/medium/campaign.
      const src = getSignupSource();
      track("Register Success", {
        method: "email",
        source: src.source,
        medium: src.medium,
        campaign: src.campaign || "(none)",
        landing: src.landing_path || "(unknown)",
      });
      // Google Ads conversion — fire once per browser per user.
      trackAdConversionOnce(SIGNUP_ADS_CONVERSION, newUser?.user_id || form.email, { value: 1.0, currency: "EUR" });
      toast.success(t("register.welcome", "Tili luotu. Tervetuloa!"));
      navigate("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("register.failed", "Rekisteröinti epäonnistui"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      <div className="relative hidden md:flex flex-col p-12 brand-gradient text-white">
        <Link to="/" className="inline-flex items-center">
          <img
            src="/brand/veraso-logo.png"
            alt="Veraso Reports"
            className="h-9 w-auto object-contain brightness-0 invert"
          />
        </Link>
        <div className="mt-auto max-w-md">
          <div className="text-xs uppercase tracking-widest font-semibold opacity-80">{t("login.sidePromo")}</div>
          <h2 className="font-display text-4xl font-semibold mt-3 leading-tight">
            {t("register.title")}
          </h2>
          <p className="mt-4 opacity-90 leading-relaxed">
            {t("register.subtitle")}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="md:hidden mb-6 inline-flex"><Logo /></Link>
          <h1 className="font-display text-3xl font-semibold text-slate-900">{t("register.title")}</h1>
          <p className="mt-2 text-slate-600">{t("register.hasAccount")} <Link to="/login" className="text-emerald-600 font-semibold hover:underline">{t("register.signIn")}</Link></p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("register.name")}</label>
                <input required value={form.name} onChange={set("name")} className="input-base" placeholder={lang === "en" ? "John Smith" : "Matti Meikäläinen"} data-testid="register-name-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("register.companyName")}</label>
                <input value={form.company_name} onChange={set("company_name")} className="input-base" placeholder={lang === "en" ? "Company Inc." : "Yritys Oy"} data-testid="register-company-input" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("register.email")}</label>
              <input type="email" required value={form.email} onChange={set("email")} className="input-base" placeholder="name@company.com" data-testid="register-email-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("register.password")}</label>
              <input type="password" required minLength={6} value={form.password} onChange={set("password")} className="input-base" placeholder={lang === "en" ? "At least 6 characters" : "Vähintään 6 merkkiä"} data-testid="register-password-input" />
            </div>
            <button type="submit" disabled={busy} className="btn-primary w-full py-3.5" data-testid="register-submit-button">
              {busy ? t("register.submitting") : t("register.submit")}
              {!busy && <ArrowRight className="h-4 w-4" />}
            </button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
            <div className="h-px flex-1 bg-slate-200" />
            {t("login.or", "TAI")}
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <button type="button" onClick={() => { track("Register Click", { method: "google" }); googleLoginRedirect(); }} className="btn-ghost w-full py-3.5" data-testid="google-register-button">
            <img src="/brand/google-icon.svg" alt="" className="h-5" />
            {t("register.google")}
          </button>
        </div>
      </div>
    </div>
  );
}
